import React from 'react';
import toast from 'react-hot-toast';
import { Box, Text, HStack, Icon, VStack } from '@chakra-ui/react';
import { 
    MdCheckCircle, 
    MdError, 
    MdWarning,
    MdNotifications,
    MdClose
} from 'react-icons/md';

/**
 * Toast Notification Manager
 */
class ToastManager {
    /**
     * Show success toast notification
     * Used for successful operations and positive feedback
     */
    static showSuccess(message, options = {}) {
        const defaultOptions = {
            duration: 4000,
            position: 'top-right',
            style: {
                background: '#10b981',
                color: '#fff',
                borderRadius: '8px',
                fontSize: '14px',
                maxWidth: '400px',
                padding: '12px 16px'
            },
            icon: '✅'
        };

        const config = { ...defaultOptions, ...options };
        return toast.success(message, config);
    }

    /**
     * Show error toast notification
     * Used for error messages and failed operations
     */
    static showError(message, options = {}) {
        const defaultOptions = {
            duration: 6000,
            position: 'top-right',
            style: {
                background: '#ef4444',
                color: '#fff',
                borderRadius: '8px',
                fontSize: '14px',
                maxWidth: '400px',
                padding: '12px 16px'
            },
            icon: '❌'
        };

        const config = { ...defaultOptions, ...options };
        return toast.error(message, config);
    }

    /**
     * Show warning toast notification
     * Used for warnings and caution messages
     */
    static showWarning(message, options = {}) {
        const defaultOptions = {
            duration: 5000,
            position: 'top-right',
            style: {
                background: '#f59e0b',
                color: '#fff',
                borderRadius: '8px',
                fontSize: '14px',
                maxWidth: '400px',
                padding: '12px 16px'
            },
            icon: '⚠️'
        };

        const config = { ...defaultOptions, ...options };
        return toast(message, config);
    }

    /**
     * Show info toast notification
     * Used for informational messages and updates
     */
    static showInfo(message, options = {}) {
        const defaultOptions = {
            duration: 4000,
            position: 'bottom-right',
            style: {
                background: '#3b82f6',
                color: '#fff',
                borderRadius: '8px',
                fontSize: '14px',
                maxWidth: '350px',
                padding: '12px 16px'
            },
            icon: 'ℹ️'
        };

        const config = { ...defaultOptions, ...options };
        return toast(message, config);
    }

    /**
     * Show notification toast with improved reliability
     * Used for system notifications and alerts with better bottom-right positioning
     */
    static showNotification(message, type = 'info', options = {}) {
        try {
            // Use the direct methods for better reliability
            switch (type) {
                case 'success':
                    return ToastManager.showSuccess(message, {
                        position: 'bottom-right',
                        ...options
                    });
                case 'error':
                    return ToastManager.showError(message, {
                        position: 'bottom-right',
                        ...options
                    });
                case 'warning':
                    return ToastManager.showWarning(message, {
                        position: 'bottom-right',
                        ...options
                    });
                default:
                    return ToastManager.showInfo(message, {
                        position: 'bottom-right',
                        ...options
                    });
            }
        } catch (error) {
            console.error('ToastManager.showNotification failed:', error);
            // Fallback to basic toast
            return toast(message, {
                position: 'bottom-right',
                duration: 5000,
                ...options
            });
        }
    }

    /**
     * Show login/logout toast
     * Used for authentication status messages
     */
    static showAuthToast(message, type = 'info', options = {}) {
        const defaultOptions = {
            duration: 3000,
            position: 'bottom-center',
            style: {
                background: type === 'success' ? '#4318ff' : '#ef4444',
                color: '#fff',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '500',
                padding: '16px 24px'
            }
        };

        const config = { ...defaultOptions, ...options };
        return toast(message, config);
    }

    /**
     * Show loading toast with custom content
     * Used for operations that take time to complete
     */
    static showLoading(message, options = {}) {
        const defaultOptions = {
            duration: Infinity,
            position: 'top-right',
            style: {
                background: '#4318ff',
                color: '#fff',
                borderRadius: '8px',
                fontSize: '14px',
                maxWidth: '400px',
                padding: '12px 16px'
            },
            icon: '🔄'
        };

        const config = { ...defaultOptions, ...options };
        return toast.loading(message, config);
    }

    /**
     * Dismiss specific toast by ID
     * Provides programmatic control over toast visibility
     */
    static dismiss(toastId) {
        toast.dismiss(toastId);
    }

    /**
     * Dismiss all toasts
     * Clears all visible toasts from screen
     */
    static dismissAll() {
        toast.dismiss();
    }

    /**
     * Show custom toast with full control
     * For special cases requiring complete customization
     */
    static showCustom(message, options = {}) {
        return toast(message, options);
    }

    /**
     * Show promise-based toast for async operations
     * Automatically shows loading, success, and error states
     */
    static showPromise(promise, messages = {}) {
        const defaultMessages = {
            loading: 'Processing...',
            success: 'Operation completed successfully',
            error: 'Operation failed'
        };

        const config = { ...defaultMessages, ...messages };

        return toast.promise(
            promise,
            {
                loading: config.loading,
                success: config.success,
                error: config.error,
            },
            {
                style: {
                    borderRadius: '8px',
                    fontSize: '14px',
                    maxWidth: '400px',
                    padding: '12px 16px'
                },
                success: {
                    duration: 4000,
                    style: {
                        background: '#10b981',
                        color: '#fff'
                    }
                },
                error: {
                    duration: 6000,
                    style: {
                        background: '#ef4444',
                        color: '#fff'
                    }
                },
                loading: {
                    style: {
                        background: '#4318ff',
                        color: '#fff'
                    }
                }
            }
        );
    }

    /**
     * Show action toast with button
     * Allows user interaction within the toast
     */
    static showAction(message, actionText, onAction, options = {}) {
        const defaultOptions = {
            duration: 8000,
            position: 'bottom-right',
            style: {
                background: '#2d3748',
                color: '#fff',
                borderRadius: '8px',
                fontSize: '14px',
                maxWidth: '400px',
                padding: '12px 16px'
            }
        };

        const config = { ...defaultOptions, ...options };

        return toast.custom((t) => (
            <Box
                bg={config.style.background}
                color={config.style.color}
                borderRadius={config.style.borderRadius}
                p={3}
                maxW={config.style.maxWidth}
                opacity={t.visible ? 1 : 0}
                transition="opacity 0.2s ease-in-out"
            >
                <HStack spacing={3} align="center" w="full">
                    <Text fontSize="sm" flex={1}>
                        {message}
                    </Text>
                    <HStack spacing={2}>
                        <Box
                            as="button"
                            fontSize="sm"
                            fontWeight="medium"
                            color="#4318ff"
                            cursor="pointer"
                            _hover={{ textDecoration: 'underline' }}
                            onClick={() => {
                                onAction();
                                toast.dismiss(t.id);
                            }}
                        >
                            {actionText}
                        </Box>
                        <Icon
                            as={MdClose}
                            w={4}
                            h={4}
                            color="gray.400"
                            cursor="pointer"
                            onClick={() => toast.dismiss(t.id)}
                            _hover={{ color: 'gray.200' }}
                        />
                    </HStack>
                </HStack>
            </Box>
        ), config);
    }

    /**
     * Get toast status and counts
     * Useful for debugging and monitoring
     */
    static getStatus() {
        const toasts = toast.getVisible();
        return {
            visible: toasts.length,
            toasts: toasts.map(t => ({
                id: t.id,
                type: t.type,
                message: t.message,
                visible: t.visible
            }))
        };
    }

    /**
     * Configure global toast settings
     * Sets default configuration for all toasts
     */
    static configure(globalConfig = {}) {
        const defaultGlobalConfig = {
            position: 'top-right',
            reverseOrder: false,
            gutter: 8,
            containerStyle: {},
            containerClassName: '',
            toastOptions: {
                className: '',
                duration: 4000,
                style: {
                    background: '#363636',
                    color: '#fff',
                    borderRadius: '8px',
                    fontSize: '14px'
                },
                success: {
                    duration: 4000,
                    iconTheme: {
                        primary: '#10b981',
                        secondary: '#fff'
                    },
                    style: {
                        background: '#10b981',
                        color: '#fff'
                    }
                },
                error: {
                    duration: 6000,
                    iconTheme: {
                        primary: '#ef4444',
                        secondary: '#fff'
                    },
                    style: {
                        background: '#ef4444',
                        color: '#fff'
                    }
                },
                loading: {
                    iconTheme: {
                        primary: '#4318ff',
                        secondary: '#fff'
                    },
                    style: {
                        background: '#4318ff',
                        color: '#fff'
                    }
                }
            }
        };

        const config = { ...defaultGlobalConfig, ...globalConfig };
        
        // Apply global configuration
        if (typeof window !== 'undefined') {
            window.ADI_TOAST_CONFIG = config;
        }

        return config;
    }
}

/**
 * React component wrapper for ToastManager
 * Provides React integration and initialization
 */
function ToastManagerComponent() {
    // This component doesn't render anything visible
    // It's just used to ensure ToastManager is available globally
    return null;
}

export default ToastManagerComponent;
export { ToastManager };